import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { client } from "./api-client";
import {
  type UserTransaction,
  type UserStats,
  type ListedUser,
  type User,
  type UpdateUserPassword,
  type UserWalletDetail,
  type TransactionsListResponse,
  type TransactionStatsResponse,
} from "utils/types/user.type";

export const useGetLoggedInUser = () =>
  useQuery<User, Error>(
    ["getLoggedInUser"],
    async () => {
      const adminData = await client(`admin/me`).then((res) => res.data);
      // Transform admin data to User format for backward compatibility
      const user: User = {
        fullname: adminData.name ?? "",
        image: "",
        email: adminData.email,
        username: "",
        phone: "",
        bankName: "",
        accountNumber: "",
        accountName: "",
        isAdmin: true,
        isVerified: true,
        isActive: adminData.is_active,
        isDeleted: false,
        isSuperAdmin: adminData.is_super_admin,
        createdAt: adminData.created_at,
        updatedAt: adminData.created_at,
        id: adminData._id,
        role: "Admin",
      };
      return user;
    }
  );

// New Admin Transaction Endpoints
export interface GetTransactionsListParams {
  type?: "Deposit" | "Withdraw" | "Swap" | "buy_sell" | "all";
  status?: "completed" | "pending" | "rejected" | "in-progress";
  page?: number;
  limit?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export const useGetTransactionsList = (params?: GetTransactionsListParams) =>
  useQuery<TransactionsListResponse, Error>(
    ["getTransactionsList", params],
    async () => {
      const queryParams = new URLSearchParams();
      if (params?.type) queryParams.append("type", params.type);
      if (params?.status) queryParams.append("status", params.status);
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.limit) queryParams.append("limit", params.limit.toString());
      if (params?.search) queryParams.append("search", params.search);
      if (params?.startDate) queryParams.append("startDate", params.startDate);
      if (params?.endDate) queryParams.append("endDate", params.endDate);
      
      const queryString = queryParams.toString();
      const url = `admin/transactions-list${queryString ? `?${queryString}` : ""}`;
      return await client(url);
    }
  );

export const useGetTransactionStatsByType = (
  type: "Deposit" | "Withdraw" | "Buy_Sell" | "Swap"
) =>
  useQuery<TransactionStatsResponse, Error>(
    ["getTransactionStatsByType", type],
    async () => await client(`admin/transactions-stats/${type}`)
  );

// Legacy endpoints (deprecated - kept for backward compatibility)
export const useGetTransactions = (
  status: "Completed" | "Pending" | "Rejected",
  type?: "Swap" | "Withdraw" | "Withdraw Fiat" | "Mobile Money"
) =>
  useQuery<UserTransaction[], Error>(
    ["getTransactions", status, type],
    async () => {
      // Map legacy status to new API format
      const statusMap: Record<string, string> = {
        Completed: "completed",
        Pending: "pending",
        Rejected: "rejected",
      };
      
      const queryParams = new URLSearchParams();
      queryParams.append("status", statusMap[status] || status.toLowerCase());
      if (type === "Withdraw") queryParams.append("type", "Withdraw");
      else if (type === "Swap") queryParams.append("type", "Swap");
      
      const response = await client(`admin/transactions-list?${queryParams.toString()}`);
      return response.data;
    }
  );

export const useGetTransactionsByType = (
  type?: string,
  status?: string
) =>
  useQuery<UserTransaction[], Error>(
    ["getTransactionsByType", type, status],
    async () => {
      // Map legacy types and statuses
      const statusMap: Record<string, string> = {
        Completed: "completed",
        Pending: "pending",
        Rejected: "rejected",
      };
      
      const queryParams = new URLSearchParams();
      if (status) {
        queryParams.append("status", statusMap[status] || status.toLowerCase());
      }
      if (type && type !== "all") {
        const mappedType = type === "buy_sell" ? "buy_sell" : type;
        queryParams.append("type", mappedType);
      }
      
      const response = await client(`admin/transactions-list?${queryParams.toString()}`);
      return response.data;
    }
  );

export const useGetTransactionStats = () =>
  useQuery<any, Error>(
    ["getTransactionStats"],
    async () => await client(`wallet/getTransactionStats`)
  );


export const useGetStats = () =>
  useQuery<UserStats, Error>(
    ["getStats"],
    async () => await client(`wallet/getStats`)
  );

export const useGetFee = () =>
  useQuery<any, Error>(["getFee"], async () => await client(`wallet/getFee`));

export const useGetUserList = (isAdmin?: boolean) =>
  useQuery<ListedUser[], Error>(
    ["getUserList"],
    async () =>
      await client(`auth/getAllUsers${isAdmin ? "?filterOnlyAdmins=true" : ""}`)
  );

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { userId: string; isActive: boolean; isBlocked: boolean }
  >(async (data) => await client(`auth/updateUserActive`, { data }), {
    onSuccess() {
      void queryClient.invalidateQueries(["getUserList"]);
    },
  });
};


export const useGetReserves = () =>
  useQuery<any, Error>(
    ["getReserves"],
    async () => await client(`wallet/getReserves`)
  );



export const useSetFee = () => {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { feeName: string; feePercentage: number }
  >(async (data) => await client(`wallet/setFee`, { data }), {
    onSuccess() {
      void queryClient.invalidateQueries(["getFee"]);
    },
  });
};

// Removed - no endpoint to update email in backend

export const useUpdatePassword = () =>
  useMutation<unknown, Error, UpdateUserPassword>(
    async (data) => await client(`admin/me/password`, { 
      method: "PATCH",
      data: { password: data.password }
    })
  );

export const useUpdateAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { userId: string; role: "User" | "Admin" | string }
  >(async (data) => await client(`auth/updateUserRole`, { data }), {
    onSuccess() {
      void queryClient.invalidateQueries(["getUserList"]);
    },
  });
};

export const useGetUserWalletBalance = (id?: string) =>
  useQuery<UserWalletDetail, Error>(
    ["getUserWalletBalance", id],
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    async () => await client(`wallet/getUserWalletWithBalance/${id}`),
    { enabled: !!id }
  );

export const useUpdateTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { transactionId: string; status: string }>(
    async (data) => await client(`wallet/updateTransaction`, { data }),
    {
      onSuccess() {
        void queryClient.invalidateQueries(["getTransactions", "Pending"]);
      },
    }
  );
};






